import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Check, Copy, Download, ShieldCheck, Smartphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { renderSVG } from 'uqr';
import { api, ApiError, call } from '../../api/client';
import { Logo } from '../../components/Logo';
import { Badge, Card, ErrorText, Spinner } from '../../components/ui';

const DOWNLOAD_URL = '/api/v1/app-releases/android/current/download';

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** `ab12cd…` → `AB:12:CD:…`, the way Android tools print certificate fingerprints. */
export function colonHex(hex: string): string {
  return (hex.toUpperCase().match(/.{2}/g) ?? []).join(':');
}

function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-[var(--muted)]">{label}</span>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-[var(--accent)] hover:bg-[var(--surface-2)]"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <code className="mt-1 block break-all rounded-lg bg-[var(--surface-2)] px-2 py-1.5 font-mono text-xs">{value}</code>
    </div>
  );
}

/** QR code to this page (not to the APK) so desktop visitors can continue on their phone. Generated locally. */
function PageQrCode() {
  const src = useMemo(() => `data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(`${window.location.origin}/download`, { border: 1 }))}`, []);
  return (
    <Card className="hidden flex-col items-center gap-2 text-center md:flex">
      <img src={src} alt="QR code for this download page" width={148} height={148} className="rounded-lg bg-white p-1" />
      <p className="max-w-40 text-xs text-[var(--muted)]">On a computer? Scan to open this page on your Android phone.</p>
    </Card>
  );
}

/**
 * Public download page (docs/android-release.md §4): everything someone needs to install the official signed APK safely,
 * before they have an account.
 */
export function DownloadPage() {
  const { from } = useSearch({ from: '/download' });
  const current = useQuery({
    queryKey: ['app-release', 'android', 'current'],
    queryFn: () => call(api.GET('/api/v1/app-releases/{platform}/current', { params: { path: { platform: 'android' }, query: {} } })),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
  const release = current.data;

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <Link to="/" className="text-sm text-[var(--accent)] hover:underline">
        ← FamilyChores
      </Link>
      <div className="mt-4 flex items-center gap-3">
        <Logo size={48} />
        <h1 className="text-2xl font-semibold">FamilyChores for Android</h1>
      </div>
      {from === 'app' && (
        <p className="mt-2 rounded-xl bg-[var(--accent-soft)] px-3 py-2 text-sm text-[var(--accent)]">
          Download the new version below and install it over the current one — your account and household stay as they are.
        </p>
      )}

      {current.isPending ? (
        <Spinner />
      ) : current.error instanceof ApiError && current.error.status === 404 ? (
        <Card className="mt-4 flex items-center gap-4">
          <Smartphone size={32} className="text-[var(--accent)]" />
          <div>
            <p className="font-medium">Not available yet</p>
            <p className="text-sm text-[var(--muted)]">The Android app hasn't been released yet. Check back soon — you can use the web app in the meantime.</p>
          </div>
        </Card>
      ) : current.error ? (
        <ErrorText error={current.error} />
      ) : (
        release && (
          <>
            <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto]">
              <Card>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold">Version {release.versionName}</h2>
                  <Badge tone="accent">Official release</Badge>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                  <div>
                    <dt className="text-xs text-[var(--muted)]">Version code</dt>
                    <dd>{release.versionCode}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--muted)]">Released</dt>
                    <dd>{new Date(release.publishedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--muted)]">Size</dt>
                    <dd>{formatBytes(release.fileSizeBytes)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--muted)]">Requires</dt>
                    <dd>Android {release.minOsVersion}+</dd>
                  </div>
                </dl>
                <a
                  href={DOWNLOAD_URL}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent)] px-4 py-3 font-medium text-white hover:brightness-110 sm:w-auto"
                >
                  <Download size={18} /> Download Android App
                </a>
                {release.releaseNotes && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium">What's new</h3>
                    <p className="mt-1 whitespace-pre-line text-sm text-[var(--muted)]">{release.releaseNotes}</p>
                  </div>
                )}
              </Card>
              <PageQrCode />
            </div>

            <Card className="mt-4 space-y-3">
              <h2 className="flex items-center gap-2 font-semibold">
                <ShieldCheck size={18} className="text-[var(--accent)]" /> Fingerprints
              </h2>
              <CopyValue label="SHA-256 checksum of the APK" value={release.sha256} />
              <CopyValue label="Signing certificate SHA-256" value={colonHex(release.signingCertSha256)} />
            </Card>
          </>
        )
      )}

      <Card className="mt-4">
        <h2 className="font-semibold">How to install</h2>
        <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm">
          <li>
            Download only from this official page. <strong>We never send the app by email or chat</strong> — don't install a copy someone sends you.
          </li>
          <li>Open the downloaded file. Android asks you to allow installs from your browser (“Install unknown apps”).</li>
          <li>Allow it for this install, then tap Install.</li>
          <li>Recommended: turn that permission back off afterwards (Settings → Apps → your browser → Install unknown apps).</li>
          <li>Updates: the app tells you when a new version is available and brings you back to this page.</li>
        </ol>
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-medium">Google Play Protect warning?</summary>
          <p className="mt-2 text-[var(--muted)]">
            Android may warn about apps installed from outside Google Play. That's expected for apps distributed directly. If the file came from this page, the
            warning doesn't mean something is wrong — you can check the fingerprints above to be sure.
          </p>
        </details>
        <details className="mt-2 text-sm">
          <summary className="cursor-pointer font-medium">Verify your download (optional)</summary>
          <div className="mt-2 space-y-2 text-[var(--muted)]">
            <p>Compare the SHA-256 checksum of the file you downloaded with the one above. They must be identical.</p>
            <p>
              On a computer: <code className="font-mono">shasum -a 256 FamilyChores-*.apk</code> (macOS/Linux) or <code className="font-mono">certutil -hashfile FamilyChores-*.apk SHA256</code>{' '}
              (Windows). On Android, use any file-hash app.
            </p>
            <p>
              Developers can also check the signer with <code className="font-mono">apksigner verify --print-certs</code>; the certificate SHA-256 must match the signing certificate above,
              and it never changes between versions.
            </p>
          </div>
        </details>
      </Card>
    </div>
  );
}
